<template>
  <div class="crm-chat crm-chat--wa">
    <template v-if="messageList!=null && messageList.length>0">
      <div class="crm-chat__messages" ref="chatMessages">
        <div v-for="(message, index) in messageList" :key="index"
             class="crm-chat__row" :class="message.isIncoming ? 'is-in' : 'is-out'">
          <div class="crm-chat__bubble">
            <p class="crm-chat__text">{{ removeHtmlTags(message.body) }}</p>
            <span class="crm-chat__time">{{ $moment(message.date).format('DD/MM/YYYY HH:mm') }}</span>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="crm-chat__empty">
      <div class="crm-chat__empty-icon crm-chat__empty-icon--wa"><i class="fab fa-whatsapp"></i></div>
      <p>{{ msg("No conversation yet") }}</p>
    </div>

    <!-- Composer: template path (outside the 24h window) or free chat -->
    <div class="crm-chat__composer">
      <b-input
          :disabled="isInputDisabled || isTemplate"
          type="text"
          v-model="newMessage.body"
          class="crm-chat__input"
          :placeholder="msg('Type a message...')"
      />

      <template v-if="isTemplate">
        <b-button variant="success" class="crm-chat__send crm-chat__send--wide" @click="openTemplateModal">
          <i class="fas fa-file-lines mr-25"></i> {{ msg('Template') }}
        </b-button>
        <b-button
            variant="link"
            class="crm-chat__policy p-0"
            v-b-popover.click="'To message a client who hasn’t replied in the last 24 hours, WhatsApp requires using a pre-approved template first. If the client responds, you may continue chatting freely within a 24-hour window.'"
            title="WhatsApp Business Policy"
        >
          <i class="fas fa-info-circle"></i>
        </b-button>
      </template>

      <template v-else>
        <b-button
            variant="success"
            class="crm-chat__send"
            :disabled="!newMessage.body || !clientId"
            v-b-tooltip
            :title="msg('Send')"
            @click="sendMessage(false)"
        >
          <i class="fas fa-paper-plane"></i>
        </b-button>
      </template>
    </div>

    <!-- Template Picker Modal -->
    <b-modal
        id="template-picker"
        ref="templateModal"
        :title="msg('chooseawhatsapptemplate')"
        size="lg"
        hide-footer
        body-class="p-0"
        :no-close-on-esc="loadingTemplates"
        :no-close-on-backdrop="loadingTemplates"
    >
      <div class="p-3">
        <div class="d-flex flex-column flex-md-row align-items-md-center mb-3 ">

          <div class="d-flex align-items-center ml-md-2">
            <b-form-select
                v-model="templateLang"
                :options="languageOptions"
                class="mr-2"
            />
            <b-form-select
                v-model="templateCategory"
                :options="categoryOptions"
            />
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loadingTemplates" class="text-center py-4">
          <b-spinner small class="mr-2"></b-spinner>
          {{ msg('loadingtemplates') }}
        </div>

        <!-- Could not load / send: show the backend's reason, not a generic empty state -->
        <div v-else-if="templatesError" class="text-center py-4">
          <b-alert show variant="danger" class="mb-0 text-left">
            <i class="fas fa-triangle-exclamation mr-1"></i>
            <strong>{{ msg('whatsappTemplatesError') }}</strong>
            <div class="mt-50">{{ templatesError }}</div>
          </b-alert>
        </div>

        <!-- Empty -->
        <div v-else-if="filteredTemplates.length === 0" class="text-center py-4 text-muted">
          {{ msg('notemplatesfound') }}
        </div>

        <!-- Grid -->
        <div v-else class="template-grid">
          <div
              v-for="(tpl, i) in filteredTemplates"
              :key="tpl.id || tpl.name + i"
              class="template-card"
              @click="selectTemplate(tpl)"
          >
            <div class="d-flex align-items-center justify-content-between">
              <div class="font-weight-bold text-truncate">{{ tpl.name }}</div>
              <b-badge :variant="tpl.status === 'APPROVED' ? 'light-success' : 'light-secondary'">
                {{ tpl.status || 'UNKNOWN' }}
              </b-badge>
            </div>

            <div class="mt-2 small text-muted d-flex align-items-center flex-wrap">
              <span class="mr-2">
                <i class="fas fa-language mr-1"></i>{{ tpl.language || 'n/a' }}
              </span>
              <span class="mr-2">
                <i class="fas fa-folder-open mr-1"></i>{{ tpl.category || 'n/a' }}
              </span>
            </div>

            <div class="template-body mt-2">
              {{ extractBodyText(tpl) }}
            </div>

            <div class="template-actions">
              <b-button
                  variant="outline-success"
                  size="sm"
                  @click.stop="selectTemplate(tpl)"
              >
                {{msg('Use Template')}}
              </b-button>
            </div>

          </div>
        </div>

        <div class="mt-3 d-flex justify-content-end">
          <b-button variant="secondary" class="mr-2" @click="closeTemplateModal" :disabled="loadingTemplates">
            {{ msg('Close') }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {mapActions} from "vuex";

export default {
  name: "WhatsappChatComponent",
  props: ['clientId', 'reservationId'],
  watch: {
    clientId: {
      handler() {
        this.refresh();
      },
      immediate: true
    },
    whatsapp: {
      handler() {
        this.refresh();
      },
      immediate: true
    }
  },
  data: () => ({
    loading: true,
    refreshIntervalId: null,
    ready: true,
    newMessage: {body: '', reservationId: ''},
    whatsappMessage: {},
    messageList: [],

    // Templates UI
    templates: [],
    loadingTemplates: false,
    templatesError: null,
    templateSearch: '',
    templateLang: 'all',
    templateCategory: 'all',
  }),
  mounted() {
    this.startRefresh();
  },
  beforeDestroy() {
    this.stopRefresh();
  },
  computed: {
    isInputDisabled() {
      return this.messageList == null || this.messageList.length < 1;
    },
    isTemplate() {
      if (this.messageList == null || this.messageList.length < 1) return true;
      const firstItemDate = new Date(this.messageList[0].date);
      const twentyFourHoursAgo = new Date().getTime() - 24 * 60 * 60 * 1000;
      return firstItemDate.getTime() < twentyFourHoursAgo;
    },
    languageOptions() {
      const langs = Array.from(new Set((this.templates || []).map(t => t.language).filter(Boolean)));
      return [{value: 'all', text: this.msg('alllanguages')}, ...langs.map(l => ({value: l, text: l}))];
    },
    categoryOptions() {
      const cats = Array.from(new Set((this.templates || []).map(t => t.category).filter(Boolean)));
      return [{value: 'all', text: this.msg('allcategories')}, ...cats.map(c => ({value: c, text: c}))];
    },
    filteredTemplates() {
      const q = (this.templateSearch || '').toLowerCase();
      return (this.templates || [])
          .filter(t => this.templateLang === 'all' || t.language === this.templateLang)
          .filter(t => this.templateCategory === 'all' || t.category === this.templateCategory)
          .filter(t => {
            const hay = [
              t.name || '',
              this.extractBodyText(t) || '',
              t.category || '',
              t.language || ''
            ].join(' ').toLowerCase();
            return hay.includes(q);
          });
    }
  },
  methods: {
    ...mapActions('whatsapp', [
      'getMessagesByClientId',
      'sendWhatsappMessage',
      // Add this action on your store; it should return WhatsAppTemplatesResponse
      'getWhatsappTemplates'
    ]),

    removeHtmlTags(text) {
      const div = document.createElement('div');
      div.innerHTML = text;
      return div.textContent || div.innerText || '';
    },

    startRefresh() {
      this.refreshIntervalId = setInterval(() => {
        this.refresh();
      }, 10000);
    },
    stopRefresh() {
      clearInterval(this.refreshIntervalId);
    },

    getMessageClasses(message, isCrm = false) {
      return {
        'message-container': true,
        'hostaway-message': !isCrm && message.isIncoming,
        'crm-message': isCrm || message.source === 'crm'
      };
    },

    // Sending
    sendMessage(isTemplate) {
      if (isTemplate) {
        // now handled by modal -> choose a template first
        this.openTemplateModal();
        return;
      }
      this.ready = false;
      const newMessage = {
        body: this.newMessage.body,
        clientId: this.clientId
      };
      this.sendWhatsappMessage({newMessage, template: false})
          .then(this.onMessagesRetrievedAfterSend);
    },

    // Template modal controls
    async openTemplateModal() {
      this.templatesError = null;
      this.templates = [];
      this.loadingTemplates = true;
      this.$nextTick(() => this.$refs.templateModal && this.$refs.templateModal.show());
      try {
        // Lazy-load templates each time (or cache as you prefer)
        const resp = await this.getWhatsappTemplates({clientId: this.clientId});
        this.templates = (resp && resp["response"]) || [];
      } catch (e) {
        // the store already toasted; keep the reason visible inside the picker too
        this.templatesError = this.errorText(e);
      } finally {
        this.loadingTemplates = false;
      }
    },
    errorText(e) {
      if (e == null) return this.msg('Unknown error');
      if (typeof e === 'string') return e;
      const data = e.response && e.response.data;
      if (data && typeof data.message === 'string' && data.message.trim() !== '') return data.message;
      return e.message || String(e);
    },
    closeTemplateModal() {
      this.$refs.templateModal && this.$refs.templateModal.hide();
    },

    extractBodyText(tpl) {
      // WhatsApp template example: components: [{type:'HEADER'...}, {type:'BODY', text:'...'}]
      if (!tpl || !tpl.components) return '';
      const body = tpl.components.find(c => c.type === 'BODY');
      return body && body.text ? body.text : '';
    },

    async selectTemplate(tpl) {
      this.ready = false;

      // Use the template text as the body we send
      const body = this.extractBodyText(tpl) || '';

      const waReq = {
        body,                          // <-- send body even for template
        clientId: this.clientId,
        isTemplate: true,
        templateName: tpl.name,
        templateLanguage: tpl.language || 'en',
      };

      try {
        await this.sendWhatsappMessage({ newMessage: waReq, template: true });
        this.onMessagesRetrievedAfterSend({ messages: this.whatsappMessage });
        this.closeTemplateModal();
      } catch (e) {
        console.error('Failed to send template:', e);
        // keep the modal open and show why the send failed (missing Twilio/Meta credentials, Meta rejection, ...)
        this.templatesError = this.errorText(e);
      } finally {
        this.ready = true;
      }
    }
    ,

    // Fetch & refresh
    refresh() {
      if (!this.clientId) return;
      this.getMessagesByClientId(this.clientId).then(this.onMessagesRetrieved);
    },
    onMessagesRetrieved(data) {
      this.whatsappMessage = data["messages"];
      if (this.whatsappMessage == null || this.whatsappMessage.result == null) {
        this.messageList = [];
        return;
      }
      this.messageList = [...this.whatsappMessage.result].reverse();
    },
    onMessagesRetrievedAfterSend(data) {
      this.whatsappMessage = data["messages"];
      if (this.whatsappMessage == null || this.whatsappMessage.result == null) {
        this.messageList = [];
        return;
      }
      this.messageList = [...this.whatsappMessage.result].reverse();
      this.newMessage = {body: '', reservationId: ''};
      this.ready = true;
    },

    // Optional date util
    formatDate(date) {
      const options = {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
      };
      const formattedDate = new Date(date).toLocaleString('de-AT', options);
      return formattedDate.replace(',', '');
    }
  }
}
</script>

<style lang="scss">
@import '~@/assets/scss/components/crm-chat.scss';

/* WhatsApp flavour on top of the shared .crm-chat look */
.crm-chat--wa {
  // explicit selector: nesting `.is-out &` inside `&__bubble` would compile
  // with the row class OUTSIDE the component and never match
  .crm-chat__row.is-out .crm-chat__bubble {
    background: linear-gradient(135deg, #128c7e, #1fae8e);
    color: #fff;
  }

  .crm-chat__send--wide {
    width: auto;
    padding: 0 16px !important;
    font-size: 12.5px;
    font-weight: 700;
    white-space: nowrap;
  }

  .crm-chat__policy {
    align-self: center;
    font-size: 17px;
    color: #ea5455;
    flex-shrink: 0;
  }

  .crm-chat__empty-icon--wa {
    color: #25d366;
    background: rgba(37, 211, 102, .12);
    font-size: 28px;
  }
}

/* Template picker */
.template-grid {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 12px;
  align-items: stretch;
}

@media (min-width: 576px) {
  .template-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 992px) {
  .template-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.template-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid rgba(34, 41, 47, .09);
  border-radius: 12px;
  padding: 14px;
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.1s ease, border-color 0.15s ease;
}

.template-card:hover {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  border-color: rgba(37, 211, 102, .5);
  transform: translateY(-1px);
}

.template-body {
  font-size: 0.9rem;
  line-height: 1.3rem;
  white-space: pre-wrap;
}

.template-actions {
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
  padding-top: 10px;
}

.dark-layout .template-card {
  border-color: rgba(255, 255, 255, .09);
}

.dark-layout .crm-chat--wa .crm-chat__empty-icon--wa {
  background: rgba(37, 211, 102, .18);
}
</style>
